import { TabPanel } from "@mui/lab";
import { Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useLocation, useParams, useSearchParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { WorkflowTemplateActivity } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import PageTabs from "../../../common/PageTabs";
import WorkflowDiagram from "./workflow-diagram/WorkflowDiagram";
import { getWorkflowGraph } from "./workflow-diagram/workflowDiagramHelper";
import { WorkflowTemplateDetailsContextProvider } from "./WorkflowTemplateDetailsContext";
import WorkflowTemplateInstructions from "./WorkflowTemplateInstructions";
import WorkflowTemplateSettings from "./WorkflowTemplateSettings";

type TabValue = "diagram" | "instructions" | "settings";

const WorkflowTemplateDetailsPage = () => {
  usePageTitle("Workflow Template");

  const { versionId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { hasPermissions } = useClientContext();

  const [selectedTab, setSelectedTab] = useState<TabValue>(() =>
    searchParams.get("tab") === "instructions" ? "instructions" : "diagram"
  );

  const getWorkflowTemplateVersionDetails = useCallback(
    () => adminApi.getWorkflowTemplateVersionDetails(versionId ?? ""),
    [versionId]
  );

  const [templateDetails, fetchError, { setData: setTemplateDetails }] = useFetch(getWorkflowTemplateVersionDetails);

  if (!versionId) {
    return <Navigate to=".." />;
  }

  if (fetchError) {
    logError(fetchError, "[WorkflowTemplateDetailsPage] getWorkflowTemplateVersionDetails");
    return <DataLoadingFailed />;
  }

  if (templateDetails === undefined) {
    return <InlineLoader />;
  }

  const handleActivityUpdated = (updatedActivity: WorkflowTemplateActivity) => {
    setTemplateDetails((prev) =>
      prev
        ? {
            ...prev,
            workflowTemplateVersion: {
              ...prev.workflowTemplateVersion,
              activities: prev.workflowTemplateVersion.activities.map((a) =>
                a.id === updatedActivity.id ? updatedActivity : a
              ),
            },
            diagramStepsMap: {
              ...prev.diagramStepsMap,
              [updatedActivity.id]: {
                ...defined(prev.diagramStepsMap[updatedActivity.id]),
                activity: updatedActivity,
              },
            },
          }
        : undefined
    );
  };

  const hasEditPermissions =
    hasPermissions(["ManageWorkflows"]) && Boolean(templateDetails.workflowTemplateVersion.isCurrent);

  const workflowGraph = getWorkflowGraph(templateDetails.diagramStepsMap);

  const showInstructionsTab = hasEditPermissions && templateDetails.workflowTemplateVersion.supervisorAgentEnabled;
  const showSettingsTab = templateDetails.workflowTemplateVersion.aiAgentTools.length > 0;

  const tabs: TabValue[] = [
    "diagram",
    ...(showInstructionsTab ? (["instructions"] as const) : []),
    ...(showSettingsTab ? (["settings"] as const) : []),
  ];

  const visibleTab = tabs.includes(selectedTab) ? selectedTab : "diagram";
  const focusPromptId = searchParams.get("prompt") ?? undefined;
  const focusQuote = new URLSearchParams(location.hash.slice(1)).get("quote") ?? undefined;

  const labels = [
    "Workflow Diagram",
    ...(showInstructionsTab ? ["Agent Instructions"] : []),
    ...(showSettingsTab ? ["Settings"] : []),
  ];

  return (
    <WorkflowTemplateDetailsContextProvider
      hasEditPermissions={hasEditPermissions}
      workflowTemplate={templateDetails.workflowTemplateVersion}
      onActivityUpdated={handleActivityUpdated}
    >
      <GeneralPageHeader
        TitleComponent={
          <Stack spacing={0.5}>
            <Typography variant="h6">{templateDetails.workflowTemplateVersion.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              Workflow Template
            </Typography>
          </Stack>
        }
        showDefaultBackButtonTo=".."
        hideBorderLine
      />
      <PageTabs<TabValue> tabs={tabs} labels={labels} value={visibleTab} onChange={(value) => setSelectedTab(value)}>
        <TabPanel value="diagram" sx={{ px: 3, py: 2.5, height: "100%" }}>
          <ScrollableFlexContainer sx={{ height: "100%" }}>
            <WorkflowDiagram
              stepsMap={templateDetails.diagramStepsMap}
              workflowGraph={workflowGraph}
              isCurrentTemplateVersion={templateDetails.workflowTemplateVersion.isCurrent}
            />
          </ScrollableFlexContainer>
        </TabPanel>
        {tabs.includes("instructions") && (
          <TabPanel value="instructions" sx={{ px: 0, py: 2.5, height: "100%" }}>
            <WorkflowTemplateInstructions
              workflowTemplate={templateDetails.workflowTemplateVersion}
              onTemplateUpdated={setTemplateDetails}
              onActivityUpdated={handleActivityUpdated}
              focusPromptId={focusPromptId}
              focusQuote={focusQuote}
            />
          </TabPanel>
        )}
        {tabs.includes("settings") && (
          <TabPanel value="settings" sx={{ px: 3, py: 2.5, height: "100%" }}>
            <WorkflowTemplateSettings workflowTemplate={templateDetails.workflowTemplateVersion} />
          </TabPanel>
        )}
      </PageTabs>
    </WorkflowTemplateDetailsContextProvider>
  );
};

export default WorkflowTemplateDetailsPage;
